import { type FC, useState, useEffect, useMemo } from "react";
import { useTransitionWithQuery } from "@userFrontend/features/publication/hooks/useTransitionWithQuery";
import { useInView } from "react-intersection-observer";
import { useDetectWindowSizeInfo } from "@root/userFrontend/hooks/useDetectWindowSize";
import { pushDataLayer } from "@root/utils/pushDataLayer";
import { useFixBackground } from "@root/shared/hooks/useFixBackground";
import { Presentation } from "./Presentation";
import { type WorkWithShopWorkCassetteProps, type ModalLinkParams } from "./type";
import { useShopWorkCassettes } from "./useShopWorks";
import {
  VISIBLE_SHOP_NUMBER_SP,
  VISIBLE_SHOP_NUMBER_PC,
  GA_SHOW_MODAL_BUTTON_IN_VIEW,
} from "./constants";

export const WorkWithShopWorkCassette: FC<WorkWithShopWorkCassetteProps> = (props) => {
  // hooks
  const {
    aggregateKey,
    clientShops,
    imgSrc,
    workCode,
    query,
    gaLabel,
    shopWorks,
    secondaryContentAsync,
  } = props;
  const { totalNumber } = clientShops;
  const { isSP } = useDetectWindowSizeInfo();
  const { shops, handleInView, isShowIndicator } = useShopWorkCassettes({
    url: secondaryContentAsync.url,
    params: secondaryContentAsync.params,
    imgSrc,
    aggregateKey,
    workCode,
    query,
    shopWorks,
    totalNumber,
  });
  const { handleClickLink } = useTransitionWithQuery();
  const { ref: indicatorRef, inView: indicatorInView } = useInView();
  const { ref: showShopWorksButtonRef, inView: showShopWorksButtonInView } = useInView();
  const { fixBody, unFixBody } = useFixBackground();

  // state
  const [isShowModal, setIsShowModal] = useState(false);

  const handleShowModal = () => {
    setIsShowModal(true);
    pushDataLayer({
      event: "modal_open",
      job_id: gaLabel,
    });
    fixBody();
  };
  const handleCloseModal = () => {
    setIsShowModal(false);
    unFixBody();
  };

  const handleClickModalLink = ({
    url,
    query: modalQuery,
    event,
    gaAction,
    gaLabel: modalGaLabel,
  }: ModalLinkParams): void => {
    pushDataLayer({
      event: gaAction || "",
      job_id: modalGaLabel || "",
    });
    handleClickLink({
      url,
      query: modalQuery,
      event,
    });
  };

  // 店舗ごと求人モーダルにて、追加の店舗ごと求人データを取得する
  // インジケーターが画面内に入ってきたら処理開始、全件取得したらインジケーターは消える
  useEffect(() => {
    if (indicatorInView) {
      handleInView();
    }
    // handleInviewを含めるとループしてしまうので除外する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indicatorInView]);

  // 他◯店舗の求人を見るボタンが画面に入ってきたらGA4イベントを送れるようにする
  useEffect(() => {
    if (showShopWorksButtonInView) {
      pushDataLayer({
        event: GA_SHOW_MODAL_BUTTON_IN_VIEW,
        job_id: gaLabel,
      });
    }
  }, [gaLabel, showShopWorksButtonInView]);

  // 画面に初期表示される店舗ごと求人の数を、画面サイズ（768pxが境目）に応じて出し分ける
  const defaultShowShopNumber = useMemo(
    () => (isSP ? VISIBLE_SHOP_NUMBER_SP : VISIBLE_SHOP_NUMBER_PC),
    [isSP],
  );

  return (
    <Presentation
      {...props}
      shopWorks={shops}
      isShowModal={isShowModal}
      handleShowModal={handleShowModal}
      handleCloseModal={handleCloseModal}
      handleClickLink={handleClickLink}
      handleClickModalLink={handleClickModalLink}
      indicatorRef={indicatorRef}
      showShopWorksButtonRef={showShopWorksButtonRef}
      defaultShowShopNumber={defaultShowShopNumber}
      hiddenShopNumber={totalNumber - defaultShowShopNumber - 1}
      gaLabel={gaLabel}
      isShowIndicator={isShowIndicator}
    />
  );
};
